/*
 * New PDP media gallery (sections/new-pdp-main.liquid).
 *
 * Registers the same <media-gallery> tag and setActiveMedia(mediaId, prepend,
 * variant) API as assets/media-gallery.js so the global.js variant flow works
 * unchanged — only ONE of the two files may be loaded on a page.
 *
 * Variant image filtering is a unified per-media rule (mediaMatchesVariant —
 * no product-level mode switch): video/model slides and media flagged
 * universal (alt token "all"/"global"/"universal", or a `__GLOBAL` filename
 * marker) always show; filenames with a `__` marker use the legacy `__<SKU>`
 * match; any other image must earn visibility through its alt — a variant
 * SKU reference (pins to those variants), or option-value segments
 * ("{Title} - Obsidian - 20oz", split on -,|;) matching the selection.
 * Alts that match nothing (including bare product titles and blank alts)
 * hide. Product-wide media must say so explicitly: "{Title} - All".
 *
 * Thumbnails live in a vertical rail with a scroll chevron. Videos autoplay
 * with sound only on explicit slide selection (thumb, arrow, swipe, lightbox);
 * implicit activations play muted; prefers-reduced-motion suppresses autoplay.
 */
if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          rail: this.querySelector('[id^="GalleryThumbnails"]'),
          modal: document.querySelector('.product-media-modal__content'),
        };
        // Must match the layout breakpoint in new-pdp-main.css: vertical rail
        // ≥990px, horizontal rail (phone layout) below.
        this.mql = window.matchMedia('(min-width: 990px)');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.optionValues = [];
        try {
          this.optionValues = (JSON.parse(this.dataset.optionValues) || []).map((option) =>
            (option.values || []).map((value) =>
              (typeof value === 'string' ? value : value.name || '').toLowerCase().trim()
            )
          );
        } catch (e) {
          // data-option-values missing or malformed — alt filtering disabled
        }
        // Bare-title alts read as product-wide; a leading title is stripped
        // before segment parsing — see mediaMatchesVariant.
        this.productTitle = (this.dataset.productTitle || '').toLowerCase().trim();
        this.variantSkus = [];
        try {
          this.variantSkus = (JSON.parse(this.dataset.variantSkus) || [])
            .filter(Boolean)
            .map((value) => String(value).toLowerCase().trim());
        } catch (e) {
          // data-variant-skus missing or malformed — alt SKU precedence disabled
        }

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener(
            'slideChanged',
            debounce(this.onSlideChanged.bind(this), 250)
          );
        }

        this.arrows = {
          prev: this.querySelector('[data-gallery-prev]'),
          next: this.querySelector('[data-gallery-next]'),
        };
        if (this.arrows.prev) {
          this.arrows.prev.addEventListener('click', this.stepActiveMedia.bind(this, -1));
        }
        if (this.arrows.next) {
          this.arrows.next.addEventListener('click', this.stepActiveMedia.bind(this, 1));
        }

        if (this.elements.rail) {
          this.elements.rail.querySelectorAll('[data-target]').forEach((thumb) => {
            const button = thumb.querySelector('button');
            if (button) {
              button.addEventListener('click', () =>
                this.setActiveMedia(thumb.dataset.target, false, undefined, true)
              );
            }
          });

          this.scrollButton = this.elements.rail.querySelector('.new-pdp-gallery__rail-scroll');
          if (this.scrollButton) {
            this.scrollButton.addEventListener('click', this.scrollRail.bind(this));
          }
          const list = this.railList();
          if (list) {
            list.addEventListener('scroll', debounce(this.updateScrollButton.bind(this), 100));
          }
          this.updateScrollButton();
        }

        // Mobile swipe indicator: 55px segment slides along a 2px track as
        // the viewer <ul> scrolls (≤989px, replaces the thumbnail rail).
        this.scrubber = this.querySelector('.new-pdp-gallery__scrubber');
        this.scrubberBar = this.scrubber
          ? this.scrubber.querySelector('.new-pdp-gallery__scrubber-bar')
          : null;
        this.sliderList = this.elements.viewer
          ? this.elements.viewer.querySelector('[id^="Slider-Gallery"]')
          : null;
        if (this.scrubber && this.sliderList) {
          this.sliderList.addEventListener('scroll', this.updateScrubber.bind(this));
          window.addEventListener('resize', debounce(this.updateScrubber.bind(this), 100));
          this.updateScrubber();
        }

        try {
          const initialOptions = JSON.parse(this.dataset.initialVariantOptions);
          if (initialOptions) {
            this.filterMediaByAlt(initialOptions, this.dataset.initialVariantSku);
            this.ensureVisibleActive();
          }
        } catch (e) {
          // no initial variant info — leave all media visible
        }

        this.enforceSingleActive();
        this.updateArrows();
        this.setupModalVideoHandling();
      }

      // Lightbox: ProductModal (global.js) marks the opened media `.active`
      // in the modal but never starts video playback, and pauseAllMedia()
      // ignores muted videos entirely. Watch the modal's `open` attribute:
      // on open, stop the gallery copy and autoplay the modal copy (with
      // controls so it can be paused/unmuted); on close, stop every modal
      // video and resume the active gallery slide.
      setupModalVideoHandling() {
        // ModalDialog re-parents itself to <body>, so search the document.
        const modal = document.querySelector('product-modal.product-media-modal');
        if (!modal) return;

        new MutationObserver(() => {
          if (modal.hasAttribute('open')) {
            this.pauseGalleryVideos();
            const activeMedia = modal.querySelector('.active');
            if (!activeMedia) return;
            let video = activeMedia.matches('video')
              ? activeMedia
              : activeMedia.querySelector('video');

            // Native Shopify videos sit in <deferred-media>: the <video>
            // only exists inside its <template> until loadContent() runs
            // (ProductModal only auto-loads YouTube embeds).
            if (!video) {
              const deferred = activeMedia.matches('deferred-media')
                ? activeMedia
                : activeMedia.querySelector('deferred-media');
              if (deferred && typeof deferred.loadContent === 'function') {
                deferred.loadContent(false);
                video = deferred.querySelector('video');
              }
            }
            if (!video) return;

            const container = video.closest('.bynder-video-container');
            if (container) container.classList.add('hide-play');
            video.setAttribute('controls', 'true');
            // reduced motion: the native controls just set are the play affordance
            if (this.reducedMotion.matches) return;
            video.play().catch(() => {
              // Autoplay with sound refused — retry muted
              video.muted = true;
              video.play().catch(() => {});
            });
          } else {
            modal.querySelectorAll('video').forEach((video) => video.pause());
            const activeSlide = this.elements.viewer.querySelector('li[data-media-id].is-active');
            // closing the lightbox is a user action — resume may keep sound
            if (activeSlide) this.playActiveMedia(activeSlide, true);
          }
        }).observe(modal, { attributes: true, attributeFilter: ['open'] });
      }

      // Slide <li>s only. The viewer also contains nested [data-media-id]
      // elements (bynder-video.liquid puts the id on the <video> tag too),
      // which must never count as slides — they broke arrow stepping by
      // making a slide's own <video> its "next" target.
      slideItems() {
        return Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
      }

      visibleSlides() {
        return this.slideItems().filter((el) => el.style.display !== 'none');
      }

      // Video/model slides — exempt from SKU filtering.
      isPlayableSlide(element) {
        return !!element.querySelector(
          'video, deferred-media, model-viewer, product-model, .bynder-video-container'
        );
      }

      // Explicitly-universal media (shows for every variant): an alt token
      // of exactly "all"/"global"/"universal", or a `__GLOBAL` filename
      // marker. Alt is the practical flag — Shopify media can't be renamed
      // after upload.
      isUniversalSlide(element) {
        const tokens = (element.dataset.mediaAlt || '')
          .toLowerCase()
          .split(/[-,|;]/)
          .map((token) => token.trim());
        if (tokens.some((token) => token === 'all' || token === 'global' || token === 'universal')) {
          return true;
        }
        const image = element.querySelector('img');
        const src = (image && image.getAttribute('src')) || '';
        return /__global/i.test(src);
      }

      // Liquid can render TWO is-active slides: the featured-media <li> and,
      // when the variant has no featured media, bynder-video.liquid's first
      // gallery video <li> both claim it. Keep the first visible one.
      enforceSingleActive() {
        const active = this.slideItems().filter((el) => el.classList.contains('is-active'));
        const keeper = active.find((el) => el.style.display !== 'none') || active[0];
        active.forEach((el) => {
          if (el !== keeper) el.classList.remove('is-active');
        });
      }

      stepActiveMedia(direction) {
        const slides = this.visibleSlides();
        const index = slides.findIndex((el) => el.classList.contains('is-active'));
        const target = slides[index + direction];
        if (target) this.setActiveMedia(target.dataset.mediaId, false, undefined, true);
      }

      updateArrows() {
        if (!this.arrows || !this.arrows.prev || !this.arrows.next) return;
        const slides = this.visibleSlides();
        const index = slides.findIndex((el) => el.classList.contains('is-active'));

        const hide = slides.length < 2;
        this.arrows.prev.classList.toggle('hidden', hide);
        this.arrows.next.classList.toggle('hidden', hide);

        const prevDisabled = index <= 0;
        const nextDisabled = index === -1 || index >= slides.length - 1;

        // Disabling the focused arrow would drop focus to <body>
        if (prevDisabled && !nextDisabled && document.activeElement === this.arrows.prev) {
          this.arrows.next.focus();
        } else if (nextDisabled && !prevDisabled && document.activeElement === this.arrows.next) {
          this.arrows.prev.focus();
        }

        this.arrows.prev.disabled = prevDisabled;
        this.arrows.next.disabled = nextDisabled;
      }

      railList() {
        return this.elements.rail
          ? this.elements.rail.querySelector('.new-pdp-gallery__rail-list')
          : null;
      }

      onSlideChanged(event) {
        const current = event.detail.currentElement;
        if (!current) return;

        // Mobile swipe: all slides are laid out in a snap row (new-pdp-main.css
        // ≤989px), so a swipe changes the visible slide without going through
        // setActiveMedia — sync is-active and start/stop videos here. Skip when
        // the slide is already active (e.g. the scroll that setActiveMedia
        // itself triggers) so videos don't restart.
        if (!this.mql.matches && current.dataset.mediaId && !current.classList.contains('is-active')) {
          this.slideItems().forEach((element) => element.classList.remove('is-active'));
          current.classList.add('is-active');
          // a swipe is an explicit slide selection
          this.playActiveMedia(current, true);
          this.updateArrows();
          this.announceLiveRegion(current);
        }

        if (!this.elements.rail) return;
        const visibleThumbs = Array.from(this.elements.rail.querySelectorAll('li')).filter(
          (el) => el.style.display !== 'none'
        );
        this.setActiveThumbnail(visibleThumbs[event.detail.currentPage - 1]);
      }

      // userInitiated gates sound-first playback; global.js passes 3 args, so
      // variant changes default to false.
      setActiveMedia(mediaId, prepend, activeVariant, userInitiated = false) {
        if (activeVariant) {
          this.filterMediaByAlt(
            [activeVariant.option1, activeVariant.option2, activeVariant.option3],
            activeVariant.sku
          );
        }

        let activeMedia = this.elements.viewer.querySelector(`li[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        // If filtering hid the requested media: on a variant change the
        // requested media is the variant's featured image, so force it
        // visible (legacy parity — important in SKU-fallback mode where
        // stale filenames can miss every match). Otherwise fall back to the
        // first visible slide, and never let the gallery go blank.
        if (activeMedia.style.display === 'none') {
          if (activeVariant) {
            this.toggleMediaItem(activeMedia, true);
          } else {
            const firstVisible = this.visibleSlides()[0];
            if (firstVisible) {
              activeMedia = firstVisible;
              mediaId = firstVisible.dataset.mediaId;
            } else {
              this.toggleMediaItem(activeMedia, true);
            }
          }
        }

        this.slideItems().forEach((element) => element.classList.remove('is-active'));
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          const activeThumbnail = this.elements.rail
            ? this.elements.rail.querySelector(`[data-target="${mediaId}"]`)
            : null;
          if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
          if (this.elements.viewer.resetPages) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          // Mobile viewer is a horizontal slider; keep the active slide in view
          if (!this.mql.matches) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
        });
        this.playActiveMedia(activeMedia, userInitiated);
        this.updateArrows();
        this.announceLiveRegion(activeMedia);

        if (!this.elements.rail) return;
        this.setActiveThumbnail(this.elements.rail.querySelector(`[data-target="${mediaId}"]`));
      }

      announceLiveRegion(activeItem) {
        const region = this.elements.liveRegion;
        if (!region || !window.accessibilityStrings || !window.accessibilityStrings.imageAvailable) {
          return;
        }
        const index = this.visibleSlides().indexOf(activeItem);
        if (index === -1) return;
        region.textContent = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          index + 1
        );
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.rail || !thumbnail) return;

        this.elements.rail
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        const button = thumbnail.querySelector('button');
        if (button) button.setAttribute('aria-current', true);

        const list = this.railList();
        if (!list) return;
        const behavior = this.reducedMotion.matches ? 'auto' : 'smooth';
        if (this.mql.matches) {
          list.scrollTo({ top: thumbnail.offsetTop, behavior });
        } else {
          list.scrollTo({ left: thumbnail.offsetLeft, behavior });
        }
      }

      // Unified per-media rule — no product-level mode switch. Filename is
      // checked first, then the alt: a `__` marker means legacy `__<SKU>`
      // matching (matches → show, doesn't → hide; alts never consulted for
      // marker-named files); otherwise the alt must EARN visibility by
      // matching something the product knows — a variant SKU (pins to
      // exactly those variants, beats color/size in the same alt), an
      // option value by RECOGNITION (whole segments split on -,|; matched
      // case-insensitively — never positional, never substring; matching
      // segments constrain that option, options with no matching segment
      // are unconstrained), or the explicit all/global flags — the
      // product-wide form is "{Title} - All" (or a bare "all"). Alts that
      // match NOTHING hide, exactly like blank alts. That includes BARE
      // product-title alts: legacy SEO practice sets alt = title on
      // color-specific shots too, so bare title can't mean product-wide.
      // Import convention: "{Title} - {Color} - {Size} - {View}"; a
      // leading title is stripped before parsing but is not required.
      mediaMatchesVariant(element, selected, sku) {
        if (this.isPlayableSlide(element)) return true;
        const image = element.querySelector('img');
        if (!image) return true;
        if (this.isUniversalSlide(element)) return true;

        const src = image.getAttribute('src') || '';
        const filename = src.split('?')[0].split('/').pop() || '';
        if (filename.includes('__')) return src.includes(`__${sku}`);

        const alt = (element.dataset.mediaAlt || '').trim().toLowerCase();
        if (!alt) return false;

        // Strip a leading product title (followed by a separator) so its
        // words can't read as segments. A BARE title alt is NOT universal:
        // legacy SEO practice sets alt = product title on color-specific
        // shots too (Wide Mouth: 25 color images, alt "Wide Mouth"), so it
        // falls through to recognition and hides. Product-wide media says
        // so explicitly — "{Title} - All", or the all/global flags.
        let remainder = alt;
        if (this.productTitle && alt.startsWith(this.productTitle)) {
          const rest = alt.slice(this.productTitle.length);
          if (/^\s*[-,|;]/.test(rest)) remainder = rest;
        }

        // SKU precedence: an alt mentioning any of the product's variant
        // SKUs pins the media to those exact variants and overrides any
        // color/size segments in the same alt. Matched against the UNSPLIT
        // remainder with boundary checks — SKUs may themselves contain
        // hyphens, which the segment splitter below would shred (and a
        // shredded fragment like "m" could false-match a size value).
        if (this.variantSkus.length) {
          const escapeRe = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const skuRefs = this.variantSkus.filter((value) =>
            new RegExp('(^|[\\s,;|-])' + escapeRe(value) + '($|[\\s,;|-])').test(remainder)
          );
          if (skuRefs.length) return skuRefs.includes((sku || '').toLowerCase());
        }

        // Recognition with a HIDE default — the alt must match something.
        const segments = remainder
          .split(/[-,|;]/)
          .map((segment) => segment.trim())
          .filter(Boolean);
        let recognizedAny = false;
        const matchesSelection = this.optionValues.every((values, index) => {
          const referenced = segments.filter((segment) => values.includes(segment));
          if (referenced.length === 0) return true; // unconstrained option
          recognizedAny = true;
          return referenced.includes(selected[index]);
        });
        return recognizedAny && matchesSelection;
      }

      filterMediaByAlt(selectedOptions, sku) {
        const items = this.slideItems();
        if (!items.length || (!sku && !(selectedOptions || []).some(Boolean))) return;
        const selected = (selectedOptions || []).map((value) =>
          (value || '').toLowerCase().trim()
        );

        items.forEach((element) =>
          this.toggleMediaItem(element, this.mediaMatchesVariant(element, selected, sku || ''))
        );
        // slider-component only recounts its pages on resize — recount now so
        // mobile swipe events (slideChanged) map to the right slide after
        // filtering changed which slides are visible.
        if (this.elements.viewer && this.elements.viewer.resetPages) {
          this.elements.viewer.resetPages();
        }
        this.updateScrollButton();
        this.updateArrows();
        this.updateScrubber();
      }

      ensureVisibleActive() {
        const active = this.elements.viewer.querySelector('li[data-media-id].is-active');
        if (active && active.style.display === 'none') {
          const firstVisible = this.visibleSlides()[0];
          if (firstVisible) {
            this.setActiveMedia(firstVisible.dataset.mediaId, false);
          } else {
            // filtering hid everything — keep the active slide visible
            this.toggleMediaItem(active, true);
          }
        }
      }

      toggleMediaItem(element, show) {
        element.style.display = show ? '' : 'none';
        const id = element.dataset.mediaId;

        if (this.elements.rail) {
          const thumb = this.elements.rail.querySelector(`[data-target="${id}"]`);
          if (thumb) thumb.style.display = show ? '' : 'none';
        }
        if (this.elements.modal) {
          const modalItem = this.elements.modal.querySelector(`[data-media-id="${id}"]`);
          if (modalItem) modalItem.style.display = show ? '' : 'none';
        }
      }

      scrollRail() {
        const list = this.railList();
        if (!list) return;

        const behavior = this.reducedMotion.matches ? 'auto' : 'smooth';
        if (this.mql.matches) {
          const max = list.scrollHeight - list.clientHeight;
          if (list.scrollTop >= max - 4) {
            list.scrollTo({ top: 0, behavior });
          } else {
            list.scrollBy({ top: list.clientHeight * 0.8, behavior });
          }
        } else {
          list.scrollBy({ left: list.clientWidth * 0.8, behavior });
        }
      }

      // Position the scrubber segment from the slider's scroll progress;
      // hide the track entirely when there's nothing to swipe through
      // (single visible slide — hidden slides take no scroll width).
      updateScrubber() {
        if (!this.scrubber || !this.sliderList) return;
        const max = this.sliderList.scrollWidth - this.sliderList.clientWidth;
        this.scrubber.classList.toggle('hidden', max <= 4);
        if (max <= 4 || !this.scrubberBar) return;
        const progress = Math.min(Math.max(this.sliderList.scrollLeft / max, 0), 1);
        const range = this.scrubber.clientWidth - this.scrubberBar.offsetWidth;
        this.scrubberBar.style.transform = `translateX(${progress * range}px)`;
      }

      updateScrollButton() {
        if (!this.scrollButton) return;
        const list = this.railList();
        if (!list) return;

        const overflowing = this.mql.matches
          ? list.scrollHeight > list.clientHeight + 4
          : list.scrollWidth > list.clientWidth + 4;
        this.scrollButton.classList.toggle('hidden', !overflowing);
      }

      // pauseAllMedia() skips videos with the muted attribute (global.js keeps
      // background loops alive) — gallery videos that fell back to muted
      // playback would keep decoding invisibly forever without this.
      pauseGalleryVideos() {
        this.elements.viewer.querySelectorAll('video').forEach((video) => video.pause());
      }

      // Mute precedence: data-user-muted (either direction) > data-is-muted >
      // implicit activation. Browsers reject unmuted play() without user
      // activation, hence the muted retry.
      playWithSound(video, userInitiated) {
        if (this.reducedMotion.matches) return;
        const preferMuted =
          video.dataset.userMuted === 'true' ||
          (video.dataset.userMuted !== 'false' &&
            (video.dataset.isMuted === 'true' || !userInitiated));
        if (preferMuted) {
          video.muted = true;
          video.play().catch(() => {});
          return;
        }
        video.muted = false;
        video.play().catch(() => {
          video.muted = true;
          video.play().catch(() => {});
        });
      }

      // Subtle play/pause + mute controls over the viewer, built lazily the
      // first time a slide's video plays (deferred-media <video>s don't
      // exist until loadContent). Shown on hover/focus on desktop, always
      // on touch (new-pdp-main.css). z-index 3 puts them above the
      // .product__media-toggle::after lightbox overlay (z-index 2), and
      // clicks stop propagating so they never open the modal.
      ensureVideoControls(slide, video) {
        if (video.dataset.hasGalleryControls) return;
        video.dataset.hasGalleryControls = 'true';

        const t = {
          play: this.dataset.textPlay || 'Play video',
          pause: this.dataset.textPause || 'Pause video',
          mute: this.dataset.textMute || 'Mute video',
          unmute: this.dataset.textUnmute || 'Unmute video',
        };

        const controls = document.createElement('div');
        controls.className = 'new-pdp-video-controls';
        controls.innerHTML = `
          <button type="button" class="new-pdp-video-controls__btn" data-video-toggle-play aria-label="${t.pause}">
            <svg data-when="playing" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M4.5 2.5h2.6v11H4.5zM8.9 2.5h2.6v11H8.9z"/></svg>
            <svg data-when="paused" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M5 2.8l8 5.2-8 5.2z"/></svg>
          </button>
          <button type="button" class="new-pdp-video-controls__btn" data-video-toggle-mute aria-label="${t.mute}">
            <svg data-when="sound" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M2 6h2.5L8 3v10L4.5 10H2z"/><path d="M10.2 5.4a3.6 3.6 0 010 5.2l-.9-.9a2.35 2.35 0 000-3.4zM11.9 3.7a6 6 0 010 8.6l-.9-.9a4.75 4.75 0 000-6.8z"/></svg>
            <svg data-when="muted" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M2 6h2.5L8 3v10L4.5 10H2z"/><path d="M10.1 6.1l1.1 1.1 1.1-1.1.8.8L12 8l1.1 1.1-.8.8-1.1-1.1-1.1 1.1-.8-.8L10.4 8 9.3 6.9z"/></svg>
          </button>`;
        slide.appendChild(controls);

        const playButton = controls.querySelector('[data-video-toggle-play]');
        const muteButton = controls.querySelector('[data-video-toggle-mute]');

        const sync = () => {
          controls.classList.toggle('is-paused', video.paused);
          controls.classList.toggle('is-muted', video.muted);
          playButton.setAttribute('aria-label', video.paused ? t.play : t.pause);
          muteButton.setAttribute('aria-label', video.muted ? t.unmute : t.mute);
        };
        ['play', 'pause', 'volumechange'].forEach((type) => video.addEventListener(type, sync));
        sync();

        playButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (video.paused) {
            this.pauseGalleryVideos();
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
        muteButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          video.muted = !video.muted;
          // remember the choice so playWithSound doesn't undo it on the
          // next slide activation
          video.dataset.userMuted = video.muted ? 'true' : 'false';
        });
      }

      // Media whose ratio matches the slide box fills it edge-to-edge like
      // an image (.new-pdp-video--fill); other ratios keep the floating
      // 24px-inset + rounded treatment from new-pdp-main.css.
      fitMediaToBox(element, slide, width, height) {
        const rect = slide.getBoundingClientRect();
        if (!rect.height || !width || !height) return;
        const boxRatio = rect.width / rect.height;
        const fills = Math.abs(width / height - boxRatio) < boxRatio * 0.05;
        element.classList.toggle('new-pdp-video--fill', fills);
      }

      playActiveMedia(activeItem, userInitiated = false) {
        window.pauseAllMedia();
        this.pauseGalleryVideos();

        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
          const poster = deferredMedia.querySelector('.deferred-media__poster img');
          if (poster && poster.naturalWidth) {
            this.fitMediaToBox(poster, activeItem, poster.naturalWidth, poster.naturalHeight);
          }
          deferredMedia.loadContent(false);
          const video = deferredMedia.querySelector('video');
          if (video) {
            // chrome-free in the gallery; the LIGHTBOX copy gets
            // controls from setupModalVideoHandling — separate element.
            // Hover exposes the custom overlay instead.
            video.controls = false;
            const sizeVideo = () =>
              this.fitMediaToBox(video, activeItem, video.videoWidth, video.videoHeight);
            if (video.readyState >= 1) sizeVideo();
            else video.addEventListener('loadedmetadata', sizeVideo, { once: true });
            this.ensureVideoControls(activeItem, video);
            this.playWithSound(video, userInitiated);
          }
        }

        const bynderVideo = activeItem.querySelector('video.bynder-video');
        if (bynderVideo) {
          const sizeBynder = () =>
            this.fitMediaToBox(bynderVideo, activeItem, bynderVideo.videoWidth, bynderVideo.videoHeight);
          if (bynderVideo.readyState >= 1) sizeBynder();
          else bynderVideo.addEventListener('loadedmetadata', sizeBynder, { once: true });
          this.ensureVideoControls(activeItem, bynderVideo);
          this.playWithSound(bynderVideo, userInitiated);
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }
    }
  );
}